import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SpinnerService } from '../../../core/shared/services/spinner.service';
import { DataLoadOptions, shouldShowGlobalSpinner } from '../../../core/shared/models/data-load-options.model';
import { extractErrorMessage } from '../../../core/shared/util/error.util';
import { DossieFiltros } from '../models/dossie-filtros.model';
import { DossieRequest } from '../models/dossie-request.model';
import { Dossie } from '../models/dossie.model';
import { PerfilDossie } from '../models/perfil-dossie.model';
import { BuscarDossieUseCase } from '../usecases/buscar-dossie.usecase';
import { CriarDossieUseCase } from '../usecases/criar-dossie.usecase';
import { EditarDossieUseCase } from '../usecases/editar-dossie.usecase';
import { ExcluirDossieUseCase } from '../usecases/excluir-dossie.usecase';
import { ListarDossiesUseCase } from '../usecases/listar-dossies.usecase';
import { ListarPerfisDossieUseCase } from '../usecases/listar-perfis-dossie.usecase';
import { DEFAULT_PAGINATION_PARAMS, PaginatedResponse } from '../../../core/shared/models/pagination.model';
import { DossieListParams } from '../models/dossie-list-params.model';

interface DossieState {
  dossies: Dossie[];
  dossieSelecionado: Dossie | null;
  dossiesComItensCarregados: ReadonlySet<number>;
  perfis: PerfilDossie[];
  loading: boolean;
  error: string | null;
  success: string | null;
  pagination: PaginatedResponse<Dossie>;
}

@Injectable({
  providedIn: 'root'
})
export class DossieViewModel {
  private readonly _state = signal<DossieState>({
    dossies: [],
    dossieSelecionado: null,
    dossiesComItensCarregados: new Set<number>(),
    perfis: [],
    loading: false,
    error: null,
    success: null,
    pagination: {
      content: [],
      page: DEFAULT_PAGINATION_PARAMS.page,
      size: DEFAULT_PAGINATION_PARAMS.size,
      totalElements: 0,
      totalPages: 0,
    }
  });

  readonly state = this._state.asReadonly();
  private readonly spinnerService = inject(SpinnerService);
  private readonly carregamentosItensDossie = new Map<number, Promise<boolean>>();

  constructor(
    private readonly listarDossiesUseCase: ListarDossiesUseCase,
    private readonly buscarDossieUseCase: BuscarDossieUseCase,
    private readonly criarDossieUseCase: CriarDossieUseCase,
    private readonly editarDossieUseCase: EditarDossieUseCase,
    private readonly excluirDossieUseCase: ExcluirDossieUseCase,
    private readonly listarPerfisDossieUseCase: ListarPerfisDossieUseCase,
  ) {}

  async carregarDossies(
    params: DossieListParams = { page: 1, size: 'all' },
    options: DataLoadOptions = {}
  ): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null }));
    const mostrarSpinner = shouldShowGlobalSpinner(options);
    if (mostrarSpinner) {
      this.spinnerService.show();
    }

    try {
      const pagina = await firstValueFrom(
        this.listarDossiesUseCase.execute(params, options.origin ?? 'user')
      );
      const dossies = pagina.content;
      this._state.update(state => ({
        ...state,
        dossies,
        pagination: pagina,
        dossiesComItensCarregados: new Set(
          dossies
            .filter(dossie => dossie.id && (dossie.itens?.length ?? 0) > 0)
            .map(dossie => dossie.id!)
        ),
        loading: false
      }));
    } catch (error) {
      this._state.update(state => ({
        ...state,
        loading: false,
        error: 'Erro ao carregar dossiês!'
      }));
    } finally {
      if (mostrarSpinner) {
        this.spinnerService.hide();
      }
    }
  }

  async carregarDossiePorId(id: number, options: DataLoadOptions = {}): Promise<boolean> {
    this._state.update(state => ({ ...state, loading: true, error: null }));
    const mostrarSpinner = shouldShowGlobalSpinner(options);
    if (mostrarSpinner) {
      this.spinnerService.show();
    }

    try {
      const dossie = await firstValueFrom(
        this.buscarDossieUseCase.execute(id, options.origin ?? 'user')
      );
      this._state.update(state => ({
        ...state,
        dossies: state.dossies.map(item => item.id === id ? { ...item, itens: dossie.itens ?? [] } : item),
        dossieSelecionado: dossie,
        dossiesComItensCarregados: new Set([...state.dossiesComItensCarregados, id]),
        loading: false
      }));
      return true;
    } catch (error) {
      this._state.update(state => ({
        ...state,
        loading: false,
        error: 'Erro ao carregar dossiê!'
      }));
      return false;
    } finally {
      if (mostrarSpinner) {
        this.spinnerService.hide();
      }
    }
  }

  async carregarItensDossieSeNecessario(id: number, options: DataLoadOptions = {}): Promise<boolean> {
    const dossie = this.retornaDossieDoStatePorId(id);

    if (this.state().dossiesComItensCarregados.has(id)) {
      return true;
    }

    if ((dossie?.itens?.length ?? 0) > 0) {
      this.marcarItensDossieComoCarregados(id);
      return true;
    }

    const carregamentoEmAndamento = this.carregamentosItensDossie.get(id);
    if (carregamentoEmAndamento) {
      return carregamentoEmAndamento;
    }

    const carregamento = this.carregarDossiePorId(id, options).finally(() => {
      this.carregamentosItensDossie.delete(id);
    });

    this.carregamentosItensDossie.set(id, carregamento);
    return carregamento;
  }

  private marcarItensDossieComoCarregados(id: number): void {
    this._state.update(state => ({
      ...state,
      dossiesComItensCarregados: new Set([...state.dossiesComItensCarregados, id])
    }));
  }

  public retornaDossieDoStatePorId(id: number): Dossie | null {
    return this.state().dossies.find(dossie => dossie.id === id) ?? null
  }

  async criarDossie(payload: DossieRequest): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null, success: null }));
    this.spinnerService.show();

    try {
      await firstValueFrom(this.criarDossieUseCase.execute(payload));
      await this.carregarDossies()
      this._state.update(state => ({
        ...state,
        loading: false,
        success: 'Dossiê criado com sucesso!'
      }));
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao criar dossiê!')
      this._state.update(state => ({
        ...state,
        loading: false,
        error: msgErro
      }));
    } finally {
      this.spinnerService.hide();
    }
  }

  async editarDossie(id: number, payload: DossieRequest): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null, success: null }));
    this.spinnerService.show();

    try {
      await firstValueFrom(this.editarDossieUseCase.execute(id, payload));
      await this.carregarDossies();
      this._state.update(state => ({
        ...state,
        loading: false,
        success: 'Dossiê editado com sucesso!'
      }));
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao editar dossiê!')
      this._state.update(state => ({
        ...state,
        loading: false,
        error: msgErro
      }));
    } finally {
      this.spinnerService.hide();
    }
  }

  async excluirDossie(id: number): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null, success: null }));
    this.spinnerService.show();

    try {
      await firstValueFrom(this.excluirDossieUseCase.execute(id));
      this._state.update(state => ({
        ...state,
        dossies: state.dossies.filter(dossie => dossie.id !== id),
        dossieSelecionado: state.dossieSelecionado?.id === id ? null : state.dossieSelecionado,
        loading: false,
        success: 'Dossiê excluído com sucesso!'
      }));
    } catch (error) {
      const msgErro = extractErrorMessage(error, 'Erro ao excluir dossiê!')
      this._state.update(state => ({
        ...state,
        loading: false,
        error:  msgErro
      }));
    } finally {
      this.spinnerService.hide();
    }
  }

  limparMensagens(): void {
    this._state.update(state => ({
      ...state,
      error: null,
      success: null
    }));
  }

  selecionarDossie(dossie: Dossie): void {
    this._state.update(state => ({
      ...state,
      dossieSelecionado: dossie
    }));
  }

  limparDossieSelecionado(): void {
    this._state.update(state => ({
      ...state,
      dossieSelecionado: null
    }));
  }

  async carregarPerfis(): Promise<void> {
    this._state.update(state => ({ ...state, loading: true, error: null }));
    this.spinnerService.show();

    try {
      const perfis = await firstValueFrom(this.listarPerfisDossieUseCase.execute());
      this._state.update(state => ({
        ...state,
        perfis,
        loading: false
      }));
    } catch (error) {
      this._state.update(state => ({
        ...state,
        loading: false,
        error: 'Erro ao carregar perfis!'
      }));
    } finally {
      this.spinnerService.hide();
    }
  }
}
